(() => {
  let measurementsTrigger = null;
  let measurementsCloseTimer = null;

  const formatHardwareSizeLabel = (value) => {
    const rawValue = String(value || '').trim();
    const measurements = [...rawValue.matchAll(/(\d+(?:\.\d+)?)\s*mm\b/gi)];
    const overallLength = measurements.at(-1)?.[1];
    return overallLength ? `${overallLength} mm overall` : rawValue;
  };

  document.addEventListener('variant:change', ({ detail }) => {
    if (!detail?.sectionId || !detail.html) return;
    const galleryId = `MediaGallery-${detail.sectionId}`;
    const gallery = document.getElementById(galleryId);
    const nextBadge = detail.html.getElementById(galleryId)?.querySelector('.product__badges');
    const currentBadge = gallery?.querySelector('.product__badges');
    if (!gallery) return;
    if (currentBadge && nextBadge) currentBadge.replaceWith(nextBadge.cloneNode(true));
    else if (currentBadge) currentBadge.remove();
    else if (nextBadge) gallery.prepend(nextBadge.cloneNode(true));
  });

  const prepareMeasurementDrawers = () => {
    document.querySelectorAll('[data-zano-measurements-drawer]').forEach((drawer) => {
      if (drawer.dataset.measurementsReady === 'true') return;
      drawer.dataset.measurementsReady = 'true';

      drawer.querySelectorAll('.zano-measurements-drawer__content p').forEach((paragraph) => {
        const dimensions = paragraph.textContent.match(/Size:\s*C\s*C\s*(\d+(?:\.\d+)?)\s*\*\s*L\s*(\d+(?:\.\d+)?)\s*\*\s*H\s*(\d+(?:\.\d+)?)\s*mm/i);
        if (!dimensions) return;

        const [, fixingCentres, overallLength, projection] = dimensions;
        paragraph.innerHTML = paragraph.innerHTML.replace(
          /Size:\s*C\s*C\s*\d+(?:\.\d+)?\s*\*\s*L\s*\d+(?:\.\d+)?\s*\*\s*H\s*\d+(?:\.\d+)?\s*mm/i,
          `<span class="zano-measurement-values"><span><strong>Overall length</strong>${overallLength} mm</span><span><strong>Fixing centres</strong>${fixingCentres} mm</span><span><strong>Projection</strong>${projection} mm</span></span>`,
        );

        const drawingLink = paragraph.querySelector('a');
        const drawingTitle = drawingLink?.querySelector('strong');
        if (drawingTitle) drawingTitle.textContent = `Technical drawing — ${overallLength} mm overall`;
      });
    });
  };

  prepareMeasurementDrawers();

  const openMeasurementsDrawer = (trigger) => {
    const drawerTarget = trigger.dataset.zanoDrawerTarget || trigger.getAttribute('href');
    const drawer = drawerTarget?.startsWith('#') ? document.querySelector(drawerTarget) : null;
    if (!(drawer instanceof HTMLDialogElement)) return;

    window.clearTimeout(measurementsCloseTimer);
    measurementsTrigger = trigger;
    if (!drawer.open) drawer.showModal();
    document.body.classList.add('zano-measurements-open');
    requestAnimationFrame(() => drawer.classList.add('is-open'));
  };

  const closeMeasurementsDrawer = (drawer, restoreFocus = true) => {
    if (!(drawer instanceof HTMLDialogElement) || !drawer.open) return;
    drawer.classList.remove('is-open');
    document.body.classList.remove('zano-measurements-open');

    const finishClose = () => {
      if (drawer.open) drawer.close();
      if (restoreFocus) measurementsTrigger?.focus();
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) finishClose();
    else measurementsCloseTimer = window.setTimeout(finishClose, 220);
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-zano-measurements-drawer-trigger]');
    if (trigger) {
      event.preventDefault();
      openMeasurementsDrawer(trigger);
      return;
    }

    const closeButton = event.target.closest('[data-zano-measurements-drawer-close]');
    if (closeButton) {
      closeMeasurementsDrawer(closeButton.closest('[data-zano-measurements-drawer]'));
      return;
    }

    const drawer = event.target.closest('[data-zano-measurements-drawer]');
    if (drawer && event.target === drawer) closeMeasurementsDrawer(drawer);
  });

  document.addEventListener('cancel', (event) => {
    if (!event.target.matches?.('[data-zano-measurements-drawer]')) return;
    event.preventDefault();
    closeMeasurementsDrawer(event.target);
  }, true);

  let pendingCustomSelectName = '';

  if (!customElements.get('zano-custom-select')) {
    class ZanoCustomSelect extends HTMLElement {
      connectedCallback() {
        if (this.isInitialized) return;
        this.isInitialized = true;
        this.nativeSelect = this.querySelector('select');
        this.trigger = this.querySelector('.zano-custom-select__trigger');
        this.valueElement = this.querySelector('[data-zano-custom-select-value]');
        this.optionsPanel = this.querySelector('.zano-custom-select__options');
        this.optionButtons = [...this.querySelectorAll('.zano-custom-select__option')];
        if (!this.nativeSelect || !this.trigger || !this.optionsPanel) return;

        this.onDocumentClick = (event) => {
          if (!this.contains(event.target)) this.close(false);
        };

        this.trigger.hidden = false;
        this.classList.add('is-ready');
        this.syncFromNative();
        this.trigger.addEventListener('click', () => this.toggle());
        this.trigger.addEventListener('keydown', (event) => this.onTriggerKeydown(event));
        this.optionsPanel.addEventListener('keydown', (event) => this.onOptionsKeydown(event));
        this.optionButtons.forEach((option) => {
          option.addEventListener('click', () => this.selectOption(option));
        });
        this.nativeSelect.addEventListener('change', () => this.syncFromNative());
        document.addEventListener('click', this.onDocumentClick);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
      }

      enabledOptions() {
        return this.optionButtons.filter((option) => !option.disabled);
      }

      syncFromNative() {
        const selectedValue = this.nativeSelect.value;
        if (this.valueElement) this.valueElement.textContent = formatHardwareSizeLabel(selectedValue);
        this.optionButtons.forEach((option) => {
          const isSelected = option.dataset.value === selectedValue;
          option.classList.toggle('is-selected', isSelected);
          option.setAttribute('aria-selected', String(isSelected));
        });
      }

      toggle() {
        if (this.classList.contains('is-open')) this.close(false);
        else this.open();
      }

      open(focusLast = false) {
        this.classList.add('is-open');
        this.trigger.setAttribute('aria-expanded', 'true');
        this.optionsPanel.hidden = false;
        const enabled = this.enabledOptions();
        const selected = enabled.find((option) => option.classList.contains('is-selected'));
        (focusLast ? enabled.at(-1) : selected || enabled[0])?.focus();
      }

      close(returnFocus = true) {
        if (!this.classList.contains('is-open')) return;
        this.classList.remove('is-open');
        this.trigger.setAttribute('aria-expanded', 'false');
        this.optionsPanel.hidden = true;
        if (returnFocus) this.trigger.focus();
      }

      selectOption(option) {
        if (option.disabled) return;
        this.nativeSelect.value = option.dataset.value;
        this.syncFromNative();
        this.close(false);
        pendingCustomSelectName = this.nativeSelect.name;
        this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }

      moveFocus(event, amount) {
        const enabled = this.enabledOptions();
        const currentIndex = enabled.indexOf(document.activeElement);
        const nextIndex = Math.min(enabled.length - 1, Math.max(0, currentIndex + amount));
        enabled[nextIndex]?.focus();
        event.preventDefault();
      }

      onTriggerKeydown(event) {
        if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.open();
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          this.open(true);
        }
      }

      onOptionsKeydown(event) {
        if (event.key === 'ArrowDown') this.moveFocus(event, 1);
        else if (event.key === 'ArrowUp') this.moveFocus(event, -1);
        else if (event.key === 'Home') {
          event.preventDefault();
          this.enabledOptions()[0]?.focus();
        } else if (event.key === 'End') {
          event.preventDefault();
          this.enabledOptions().at(-1)?.focus();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          this.close(true);
        } else if (event.key === 'Tab') {
          this.close(false);
        }
      }
    }

    customElements.define('zano-custom-select', ZanoCustomSelect);

    document.addEventListener('variant:change', () => {
      if (!pendingCustomSelectName) return;
      const selectName = pendingCustomSelectName;
      pendingCustomSelectName = '';
      requestAnimationFrame(() => {
        const nextSelect = [...document.querySelectorAll('zano-custom-select select')]
          .find((select) => select.name === selectName);
        nextSelect?.closest('zano-custom-select')?.querySelector('.zano-custom-select__trigger')?.focus();
      });
    });
  }

  if (customElements.get('zano-multi-size-order')) return;

  class ZanoMultiSizeOrder extends HTMLElement {
    connectedCallback() {
      this.inputs = [...this.querySelectorAll('[data-variant-id]')];
      this.countElement = this.querySelector('[data-zano-multi-count]');
      this.totalElement = this.querySelector('[data-zano-multi-total]');
      this.statusElement = this.querySelector('[data-zano-multi-status]');
      this.submitButton = this.querySelector('[data-zano-multi-submit]');
      this.currency = this.dataset.currency || window.Shopify?.currency?.active || 'AUD';
      this.preorderMessage = this.dataset.preorderMessage || '';

      this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        style: 'currency',
        currency: this.currency,
      });

      this.addEventListener('input', () => this.updateSummary());
      this.submitButton?.addEventListener('click', () => this.addSelectedSizes());
      this.updateSummary();
    }

    selectedItems() {
      return this.inputs.flatMap((input) => {
        const quantity = Math.max(0, Number.parseInt(input.value, 10) || 0);
        if (!quantity || input.disabled) return [];
        return [{
          id: Number(input.dataset.variantId),
          quantity,
          price: Number(input.dataset.price),
          minimum: Number(input.dataset.minimumOrder) || 1,
          increment: Number(input.step) || 1,
        }];
      });
    }

    updateSummary() {
      const items = this.selectedItems();
      const count = items.reduce((sum, item) => sum + item.quantity, 0);
      const total = items.reduce((sum, item) => sum + item.quantity * item.price, 0);

      if (this.countElement) this.countElement.textContent = String(count);
      if (this.totalElement) this.totalElement.textContent = this.formatter.format(total / 100);
      if (this.submitButton) this.submitButton.disabled = count === 0;
      this.setStatus('');
    }

    validate(items) {
      for (const item of items) {
        if (item.quantity < item.minimum) {
          return `The minimum order quantity for that size is ${item.minimum}.`;
        }
        if ((item.quantity - item.minimum) % item.increment !== 0) {
          return `That size must be ordered in increments of ${item.increment}.`;
        }
      }
      return '';
    }

    setStatus(message) {
      if (!this.statusElement) return;
      this.statusElement.textContent = message;
      this.statusElement.hidden = !message;
    }

    async addSelectedSizes() {
      const items = this.selectedItems();
      const validationMessage = this.validate(items);
      if (validationMessage) {
        this.setStatus(validationMessage);
        return;
      }

      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-busy', 'true');
      this.setStatus('');

      try {
        const response = await fetch(window.routes?.cart_add_url || `${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: items.map(({ id, quantity }) => {
              const item = { id, quantity };
              if (this.preorderMessage) item.properties = { 'Pre-order': this.preorderMessage };
              return item;
            }),
          }),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to add these sizes.');

        window.location.assign(window.routes?.cart_url || `${window.Shopify.routes.root}cart`);
      } catch (error) {
        this.setStatus(error.message || 'Unable to add these sizes. Please try again.');
        this.submitButton.disabled = false;
        this.submitButton.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('zano-multi-size-order', ZanoMultiSizeOrder);
})();
